import { useEffect, useMemo, useState } from 'react';
import { Fingerprint, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { useQmsWorkflowAction } from '../../hooks/useQmsTicket';
import type {
  QmsTicket,
  QmsTicketStatus,
  QmsWorkflowAction,
} from '../../types/qms.types';
import {
  getQmsStatusLabel,
  getQmsWorkflowMeaning,
} from '../../utils/workflow';

interface QmsWorkflowSignatureDialogProps {
  open: boolean;
  ticket: QmsTicket;
  action: QmsWorkflowAction;
  targetStatus?: QmsTicketStatus;
  onOpenChange: (open: boolean) => void;
  onCompleted?: (ticket: QmsTicket) => void;
}

const ACTION_TITLES: Record<QmsWorkflowAction, string> = {
  transition: 'Sign quality-event state change',
  approve_change: 'Approve change control',
  reject_change: 'Reject change control',
  verify_effectiveness: 'Verify CAPA effectiveness',
};

export function QmsWorkflowSignatureDialog({
  open,
  ticket,
  action,
  targetStatus,
  onOpenChange,
  onCompleted,
}: QmsWorkflowSignatureDialogProps) {
  const { user } = useSession();
  const mutation = useQmsWorkflowAction();
  const defaultUsername = user?.username || user?.email || '';
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [metrics, setMetrics] = useState('');
  const meaning = getQmsWorkflowMeaning(action, targetStatus, ticket.status);

  useEffect(() => {
    if (!open) return;
    setUsername(defaultUsername);
    setPassword('');
    setReason('');
    setMetrics('');
    mutation.reset();
    // mutation is stable for the mounted dialog; resetting only when a new action opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, defaultUsername, open, targetStatus]);

  const errorDetails = useMemo(() => {
    if (!(mutation.error instanceof ApiError)) return [];
    const data = mutation.error.data;
    if (!data || typeof data !== 'object' || !('details' in data)) return [];
    return Array.isArray(data.details) ? data.details.map(String) : [];
  }, [mutation.error]);

  const canSubmit = username.trim().length > 0
    && password.length > 0
    && reason.trim().length >= 3
    && (action !== 'verify_effectiveness' || metrics.trim().length >= 3);

  const handleConfirm = async () => {
    const updated = await mutation.mutateAsync({
      ticketId: ticket._id,
      payload: {
        action,
        meaning,
        reason: reason.trim(),
        username: username.trim(),
        password,
        organisation: ticket.organisation,
        ...(targetStatus ? { target_status: targetStatus } : {}),
        ...(action === 'verify_effectiveness' ? { metrics: metrics.trim() } : {}),
      },
    });
    onCompleted?.(updated);
    onOpenChange(false);
  };

  const transitionDescription = action === 'transition' && targetStatus
    ? `${getQmsStatusLabel(ticket.status)} → ${getQmsStatusLabel(targetStatus)}`
    : ticket.ticket_id || ticket.title;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
            <Fingerprint className="h-5 w-5" />
          </div>
          <DialogTitle>{ACTION_TITLES[action]}</DialogTitle>
          <DialogDescription>
            {transitionDescription}. Your credentials, intent, reason, and timestamp become
            permanent audit evidence.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-teal-100 bg-teal-50/70 px-3 py-2 text-sm text-teal-900">
            <div className="flex items-center gap-2 font-medium">
              <ShieldCheck className="h-4 w-4" />
              Signature meaning: {meaning}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="qms-signature-reason">Reason and evidence basis</Label>
            <Textarea
              id="qms-signature-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="State why this action is appropriate and what evidence you reviewed…"
              rows={3}
            />
          </div>

          {action === 'verify_effectiveness' && (
            <div className="space-y-2">
              <Label htmlFor="qms-effectiveness-metrics">Effectiveness metrics</Label>
              <Textarea
                id="qms-effectiveness-metrics"
                value={metrics}
                onChange={(event) => setMetrics(event.target.value)}
                placeholder="Record the monitored metric, target, period, and observed result…"
                rows={4}
              />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="qms-signature-username">Username</Label>
              <Input
                id="qms-signature-username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="qms-signature-password">Password</Label>
              <Input
                id="qms-signature-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </div>
          </div>

          {mutation.error && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <p className="font-medium">{mutation.error.message}</p>
              {errorDetails.length > 1 && (
                <ul className="mt-1 list-disc space-y-0.5 pl-5">
                  {errorDetails.slice(1).map((detail) => <li key={detail}>{detail}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit || mutation.isPending}
            className="bg-teal-700 hover:bg-teal-800"
          >
            {mutation.isPending ? 'Signing…' : 'Sign and confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
